import React, { useState, useEffect } from "react";
import docicon from "../assets/docicon.png";

const CheckEligibility: React.FC = () => {
  const [redirectUrl, setRedirectUrl] = useState("#");

  useEffect(() => {
    // Safely extract the redirect URL from an existing anchor tag in the DOM
    const existingAnchor = document.querySelector('a[href*="http"]');
    if (existingAnchor) {
      const href = existingAnchor.getAttribute("href");
      if (href) {
        setRedirectUrl(href);
      }
    }
  }, []);

  const requirements = [
    { label: "AGE", value: "21 – 60 years" },
    { label: "MINIMUM SALARY", value: "₹35,000 / month" },
    { label: "EMPLOYMENT", value: "Salaried professionals" },
    { label: "CREDIT SCORE", value: "600+" }
  ];

  const documents = [
    { icon: "💳", label: "PAN Card" },
    { icon: "📋", label: "Aadhaar" },
    { icon: "💼", label: "Salary Slips" },
    { icon: "🏦", label: "Bank Statement" }
  ];

  const stats = [
    { value: "60 sec", label: "TO CHECK ELIGIBILITY" },
    { value: "Zero", label: "CREDIT SCORE IMPACT" },
{ value: "24/7", label: "PROCESSING ACTIVE" }
  ];

  return (
    <section id="eligibility" className="w-full py-16 sm:py-20 md:py-24 px-6 lg:px-20 font-sans bg-[#EAF1F6] flex flex-col items-center">
      <div className="max-w-5xl mx-auto w-full flex flex-col gap-10 sm:gap-12">
        
        {/* Header Row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-8 text-center md:text-left">
          <div className="flex flex-col gap-2">
            <h2 className="text-3xl sm:text-4xl md:text-[45px] font-black text-[#0F172A] tracking-tight leading-none">
              Check Your <span className="text-[var(--primary)]">Eligibility</span>
            </h2>
          </div>
          <div className="max-w-[320px] mx-auto md:mx-0 md:text-right">
            <p className="text-slate-500 font-medium italic text-[13px] sm:text-[14px] leading-relaxed">
              "Designed for India's working professionals. If you earn a salary, you're likely qualified."
            </p>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Left Column: Requirements & Documents Card (7 cols) */}
          <div className="lg:col-span-7 bg-white border border-slate-100/80 rounded-[24px] p-6 sm:p-8 shadow-[0_10px_25px_rgba(0,0,0,0.02)] flex flex-col justify-between gap-8 relative overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-[4px] bg-[var(--primary)]" />
            
            {/* Basic Requirements Subsection */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 text-[11px] sm:text-xs font-black tracking-wider text-[var(--primary)] uppercase">
                <span className="text-[16px] leading-none">•</span>
                <span>Basic Requirements</span>
              </div>

              <div className="flex flex-col gap-3">
                {requirements.map((req, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-3.5 sm:p-4 bg-[#F8FAFC] border border-slate-100/50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      {/* Check Circle Icon */}
                      <div className="flex-shrink-0 w-[20px] h-[20px] rounded-full bg-[#41A655] flex items-center justify-center">
                        <svg className="w-[11px] h-[11px] text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-slate-400 font-extrabold text-[11px] sm:text-[12px] tracking-wider uppercase">
                        {req.label}
                      </span>
                    </div>
                    <span className="text-[#0F172A] font-black text-[13px] sm:text-[15px] pl-2 text-right">
                      {req.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents Required Subsection */}
            <div className="flex flex-col gap-4 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2 text-[11px] sm:text-xs font-black tracking-wider text-slate-400 uppercase">
                <img src={docicon} alt="" className="w-4 h-4 object-contain flex-shrink-0" />
                <span>Documents Required</span>
              </div>

              <div className="flex flex-wrap gap-2.5 sm:gap-3">
                {documents.map((doc, i) => (
                  <div 
                    key={i} 
                    className="bg-[#F8FAFC] border border-slate-100/60 rounded-xl py-2.5 px-3.5 sm:px-4 flex items-center gap-2.5 shadow-[0_2px_6px_rgba(0,0,0,0.01)] hover:border-slate-200 transition-colors select-none"
                  >
                    <span className="text-base sm:text-lg">{doc.icon}</span>
                    <span className="text-[#1E293B] font-extrabold text-[12px] sm:text-[13px]">
                      {doc.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Find Out If You Qualify & Stats (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6 justify-between">
            
            {/* Dark Navy Card */}
            <div className="bg-[#0B1220] rounded-[24px] p-6 sm:p-8 relative overflow-hidden shadow-[0_10px_25px_rgba(11,30,63,0.15)] flex flex-col justify-between flex-grow border border-white/[0.04] min-h-[260px]">
              
              {/* Rupee watermark in background */}
              <div className="absolute right-[40px] top-[10px] text-white/[0.03] text-[170px] font-black pointer-events-none select-none  z-0 leading-none">
                <svg width="100" height="138" viewBox="0 0 100 138" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_102_3918)">
<path d="M30.1068 70.938C42.8496 70.938 53.6469 62.8643 57.635 51.4834H0.924927L10.6522 32.0288H57.635C53.6469 20.7451 42.8496 12.5742 30.1068 12.5742H0.924927L10.6522 -6.88037H98.1978L88.4706 12.5742H69.016C73.296 18.216 76.3114 24.8306 77.7705 32.0288H98.1978L88.4706 51.4834H77.7705C75.6874 61.5922 70.4487 70.7787 62.8092 77.7189C55.1697 84.6591 45.5241 88.9946 35.2623 90.1007L98.1978 139.029H64.1523L0.924927 90.3925V70.938H30.1068Z" fill="white" fill-opacity="0.1"/>
</g>
<defs>
<clipPath id="clip0_102_3918">
<rect width="99.5577" height="147.074" fill="white" transform="translate(0 -10.0288)"/>
</clipPath>
</defs>
</svg>

              </div>

              <div className="relative z-10 flex flex-col gap-5">
                {/* Shield Check Icon */}
                <div className="w-11 h-11 rounded-xl bg-white/[0.07] flex items-center justify-center border border-white/[0.08]">
                  <svg className="w-5.5 h-5.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>

                <div className="flex flex-col">
                  <h3 className="text-white font-black text-2xl sm:text-[26px] tracking-tight leading-tight">
                    Find Out If <span className="text-[#41A655] block sm:inline lg:block">You Qualify</span>
                  </h3>
                  <p className="text-slate-400 font-medium text-[13px] sm:text-[14px] leading-relaxed mt-2.5">
                    Takes under 60 seconds. No credit score impact. Completely free to check.
                  </p>
                </div>
              </div>

              <div className="relative z-10 mt-6">
                <a 
                  href={redirectUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full bg-white hover:bg-slate-50 text-[#0F172A] font-black text-[12px] sm:text-[13px] tracking-wider uppercase py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99] shadow-md group select-none"
                >
                  <span>Check My Eligibility</span>
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>

            </div>

            {/* Bottom Stats Grid (1x4 on mobile, 2x2 on sm+) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <div 
                  key={i}
                  className="bg-white border border-slate-100/50 shadow-[0_4px_12px_rgba(0,0,0,0.01)] rounded-2xl p-4 sm:p-4.5 flex flex-col items-center justify-center text-center gap-0.5"
                >
                  <span className="text-[17px] sm:text-[19px] font-black text-[#41A655] tracking-tight">
                    {stat.value}
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-black tracking-widest text-slate-400 uppercase leading-none mt-1">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default CheckEligibility;
